/**
 * og-pdp-reviews.js
 * Module 12 — Real Customer Proof
 *
 * 两套模式：
 * - 占位模式：Judge.me API无数据时，显示liquid硬编码精选评价
 * - 真实模式：Judge.me API有数据时，自动接管渲染
 *
 * 真实模式两套解析逻辑：
 * - 逻辑A（格式化评价）：body末尾含 " — " 分隔符，提取身高/尺码/场景
 *   格式约定："评价正文。 — 5'6\" · Size M · Ren Faire"
 * - 逻辑B（自然评价）：body末尾无分隔符，只渲染姓名+星级+正文
 *
 * 场景标签映射：从body或occasion字段自动匹配tab筛选标签
 */

(function () {
  'use strict';

  // ── 配置读取（由liquid注入）──
  const CONFIG = window.OG_REVIEWS_CONFIG || {};
  const PRODUCT_ID = CONFIG.productId;
  const SHOP_DOMAIN = CONFIG.shopDomain;

  // ── DOM节点 ──
  const section = document.querySelector('.og-pdp-reviews');
  const grid = document.getElementById('og-reviews-grid');
  const tabs = document.querySelectorAll('.og-pdp-reviews__tab');
  const emptyMsg = document.getElementById('og-reviews-empty');

  if (!section || !grid) return;

  // ── 当前激活筛选标签 ──
  let activeFilter = 'all';

  // ============================================================
  // 1. 场景标签映射
  // ============================================================

  /**
   * 从文本中识别场景标签
   * 匹配来源：body末尾格式化信息 / Judge.me custom_fields / reviewer外部标签
   */
  function mapToFilterTag(text) {
    if (!text) return 'all';
    const t = text.toLowerCase();

    if (t.includes('ren faire') || t.includes('renaissance') || t.includes('ren-faire')) return 'ren-faire';
    if (t.includes('photo') || t.includes('shoot') || t.includes('photoshoot')) return 'photoshoots';
    if (t.includes('dance') || t.includes('ballet') || t.includes('classical') || t.includes('dancing')) return 'dance';
    if (t.includes('wedding') || t.includes('guest') || t.includes('bridal')) return 'wedding-guest';
    if (t.includes('petite') || t.includes('5\'0') || t.includes('5\'1') || t.includes('5\'2') || t.includes('5\'3')) return 'petite';
    if (t.includes('curvy') || t.includes('plus') || t.includes('plus size')) return 'curvy';

    return 'all';
  }

  // ============================================================
  // 2. 评价body解析
  // ============================================================

  /**
   * 逻辑A — 格式化评价解析
   * 格式约定：评价正文。 — 5'6" · Size M · Ren Faire
   * 返回：{ quote, height, size, occasion, tag, isFormatted: true }
   */
  function parseFormattedBody(body) {
    const SEPARATOR = ' — ';
    const sepIndex = body.lastIndexOf(SEPARATOR);

    if (sepIndex === -1) return null; // 无分隔符，不是格式化评价

    const quote = body.substring(0, sepIndex).trim();
    const metaRaw = body.substring(sepIndex + SEPARATOR.length).trim();

    // 按 · 分割meta信息
    const parts = metaRaw.split('·').map(p => p.trim());

    let height = '';
    let size = '';
    let occasion = '';

    parts.forEach(part => {
      if (part.toLowerCase().startsWith('size ')) {
        size = part.replace(/^size /i, '').trim();
      } else if (
        part.includes("'") ||
        part.toLowerCase().includes('cm') ||
        /^\d{3}cm/.test(part)
      ) {
        height = part;
      } else {
        occasion = part;
      }
    });

    const tag = mapToFilterTag(occasion) !== 'all'
      ? mapToFilterTag(occasion)
      : mapToFilterTag(metaRaw);

    return { quote, height, size, occasion, tag, isFormatted: true };
  }

  /**
   * 逻辑B — 自然评价降级处理
   * 无格式约定，只渲染正文，meta从custom_fields提取
   * 返回：{ quote, height, size, occasion, tag, isFormatted: false }
   */
  function parseNaturalBody(body, customFields) {
    const quote = body.trim();

    // 尝试从custom_fields提取
    const height = extractCustomField(customFields, ['height', 'tall']);
    const size = extractCustomField(customFields, ['size', 'ordered']);
    const occasion = extractCustomField(customFields, ['wear', 'occasion', 'where']);

    const tag = mapToFilterTag(occasion) !== 'all'
      ? mapToFilterTag(occasion)
      : mapToFilterTag(body); // 从正文内容猜测场景

    return { quote, height, size, occasion, tag, isFormatted: false };
  }

  /**
   * 从Judge.me custom_fields数组中提取字段值
   * @param {Array} fields - custom_fields数组
   * @param {Array} keywords - 关键词数组，任意匹配
   */
  function extractCustomField(fields, keywords) {
    if (!fields || !Array.isArray(fields)) return '';
    const field = fields.find(f =>
      f.title && keywords.some(kw => f.title.toLowerCase().includes(kw))
    );
    return field ? (field.body || '').trim() : '';
  }

  // ============================================================
  // 3. 星级渲染
  // ============================================================

  function buildStars(rating) {
    const score = Math.round(rating || 5);
    const full = Math.min(score, 5);
    const empty = 5 - full;
    return '★'.repeat(full) + '☆'.repeat(empty);
  }

  // ============================================================
  // 4. 评价卡片构建
  // ============================================================

  function buildReviewCard(review) {
    const body = review.body || '';
    const customFields = review.custom_fields || [];

    // 优先尝试逻辑A（格式化解析），降级到逻辑B（自然评价）
    const parsed = parseFormattedBody(body) || parseNaturalBody(body, customFields);

    // 媒体区域
    let mediaHtml = '';
    if (review.pictures && review.pictures.length > 0) {
      const pic = review.pictures[0];
      const imgSrc = pic.urls?.compact || pic.urls?.original || '';
      mediaHtml = `
        <div class="og-pdp-reviews__media">
          <img
            src="${escapeHtml(imgSrc)}"
            alt="${escapeHtml(review.reviewer?.name || 'Customer')} wearing Olive Girl"
            loading="lazy"
            width="600"
            height="400"
          >
        </div>`;
    } else {
      mediaHtml = `
        <div class="og-pdp-reviews__media">
          <div class="og-pdp-reviews__media-placeholder">
            <span>Customer Photo / Video</span>
          </div>
        </div>`;
    }

    // 买家meta信息拼接
    const metaParts = [escapeHtml(review.reviewer?.name || 'Customer')];
    if (parsed.height) metaParts.push(escapeHtml(parsed.height));
    if (parsed.size) metaParts.push(`Size ${escapeHtml(parsed.size)}`);
    if (parsed.occasion) metaParts.push(escapeHtml(parsed.occasion));

    return `
      <article
        class="og-pdp-reviews__card"
        data-tags="${escapeHtml(parsed.tag)}"
        data-mode="live"
      >
        ${mediaHtml}
        <div class="og-pdp-reviews__stars" aria-label="${review.rating} out of 5 stars">
          <span aria-hidden="true">${buildStars(review.rating)}</span>
        </div>
        <blockquote class="og-pdp-reviews__quote">
          "${escapeHtml(parsed.quote)}"
        </blockquote>
        <p class="og-pdp-reviews__meta">
          ${metaParts.join(' · ')}
        </p>
      </article>`;
  }

  // ============================================================
  // 5. XSS防护
  // ============================================================

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ============================================================
  // 6. Tab筛选逻辑（占位模式 + 真实模式共用）
  // ============================================================

  function setActiveTab(filterValue) {
    activeFilter = filterValue;

    tabs.forEach(tab => {
      const isActive = tab.dataset.filter === filterValue;
      tab.classList.toggle('og-pdp-reviews__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    filterCards(filterValue);
  }

  function filterCards(filterValue) {
    const cards = grid.querySelectorAll('.og-pdp-reviews__card');
    let visibleCount = 0;

    cards.forEach(card => {
      const cardTags = (card.dataset.tags || '')
        .split(',')
        .map(t => t.trim())
        .filter(Boolean);

      const isVisible = filterValue === 'all' || cardTags.includes(filterValue);

      if (isVisible) {
        card.removeAttribute('data-hidden');
        visibleCount++;
      } else {
        card.setAttribute('data-hidden', 'true');
      }
    });

    if (emptyMsg) {
      emptyMsg.style.display = visibleCount === 0 ? 'block' : 'none';
    }
  }

  // Tab点击事件
  tabs.forEach(tab => {
    tab.addEventListener('click', () => setActiveTab(tab.dataset.filter));
  });

  // ============================================================
  // 7. Judge.me API请求
  // ============================================================

  async function fetchJudgeMeReviews() {
    if (!PRODUCT_ID || !SHOP_DOMAIN) return null;

    const url = `https://judge.me/api/v1/reviews?api_token=&shop_domain=${encodeURIComponent(SHOP_DOMAIN)}&product_id=${encodeURIComponent(PRODUCT_ID)}&per_page=20&page=1`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) return null;

      const data = await response.json();
      return Array.isArray(data.reviews) ? data.reviews : null;

    } catch (err) {
      // API不可用，静默降级保持占位模式
      console.warn('[OG Reviews] Judge.me API unavailable, staying in placeholder mode.');
      return null;
    }
  }

  // ============================================================
  // 8. 真实评价渲染
  // ============================================================

  function renderLiveReviews(reviews) {
    // 清空占位卡片
    grid.innerHTML = '';

    // 渲染真实评价
    reviews.forEach(review => {
      grid.insertAdjacentHTML('beforeend', buildReviewCard(review));
    });

    // 重新应用当前筛选
    filterCards(activeFilter);
  }

  // ============================================================
  // 9. 主逻辑入口
  // ============================================================

  async function init() {
    // 立即激活占位模式tab筛选（无需等待API）
    filterCards('all');

    // 异步请求Judge.me
    const reviews = await fetchJudgeMeReviews();

    // 有真实评价 → 切换真实模式
    if (reviews && reviews.length > 0) {
      renderLiveReviews(reviews);
    }
    // 无真实评价 → 保持占位模式，不操作
  }

  init();

})();
